import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { IntlProvider } from 'react-intl';
import { COOKIE_LOCALE, DEFAULT_LOCALE, supportedLocales } from '@constants/intl/intl-constants';
import { HttpClientProvider } from '@services/http/client/http-client-provider';
import { GetMainCategoriesRemoteCall } from '@services/http/remote-calls/snf-api/categories/get-main-categories.http-remote-call';
import { GetMainTechnologiesRemoteCall } from '@services/http/remote-calls/snf-api/technologies/get-main-technologies.http-remote-call';
import { polyfill } from '../polyfills';

const SiteDataContext = createContext(null);

export function useSiteData() {
  const ctx = useContext(SiteDataContext);
  if (!ctx) {
    throw new Error('useSiteData must be used within SiteDataProvider');
  }
  return ctx;
}

async function loadMessages(locale) {
  switch (locale) {
    case supportedLocales.EN:
    case supportedLocales.EN_GB:
      return (await import('../../compiled-lang/en.json')).default;
    default:
      return (await import('../../compiled-lang/it.json')).default;
  }
}

export function SiteDataProvider({ children }) {
  const [locale] = useState(() => Cookies.get(COOKIE_LOCALE) || DEFAULT_LOCALE);
  const [messages, setMessages] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [technologyCategories, setTechnologyCategories] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await polyfill(locale);
      const msgs = await loadMessages(locale);
      if (cancelled) return;
      setMessages(msgs);

      const httpClient = HttpClientProvider.getInstance();
      const isServer = false;
      try {
        const [sc, tc] = await Promise.all([
          httpClient.execute(new GetMainCategoriesRemoteCall(locale, isServer)),
          httpClient.execute(new GetMainTechnologiesRemoteCall(locale, isServer))
        ]);
        if (cancelled) return;
        setServiceCategories(sc);
        setTechnologyCategories(tc);
      } catch {
        if (cancelled) return;
        setServiceCategories([]);
        setTechnologyCategories([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const value = useMemo(
    () => ({
      locale,
      serviceCategories,
      technologyCategories
    }),
    [locale, serviceCategories, technologyCategories]
  );

  if (!messages) {
    return null;
  }

  return (
    <SiteDataContext.Provider value={value}>
      <IntlProvider locale={locale} defaultLocale={supportedLocales.IT} messages={messages}>
        {children}
      </IntlProvider>
    </SiteDataContext.Provider>
  );
}
